import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime, formatRelativeTime } from '../../utils/dateUtils';
import { renderMarkdown } from '../AI/AskAiPanel';

const TYPE_META = {
  excubia: { label: 'Excubia', color: 'primary' },
  vendor: { label: 'Vendor', color: 'secondary' },
  ai: { label: 'AI', color: 'info' },
  automation: { label: 'Automation', color: 'warning' },
  jira: { label: 'Jira', color: 'default' },
  intel: { label: 'Intel', color: 'error' },
  audit: { label: 'Audit', color: 'default' },
};

const FILTER_GROUPS = [
  { key: 'excubia', label: 'Excubia', types: ['excubia'], color: 'primary' },
  { key: 'vendor', label: 'Vendors', types: ['vendor'], color: 'secondary' },
  { key: 'ai', label: 'AI & intel', types: ['ai', 'intel'], color: 'info' },
  { key: 'automation', label: 'Automation', types: ['automation'], color: 'warning' },
  { key: 'jira', label: 'Jira', types: ['jira'], color: 'default' },
  { key: 'audit', label: 'Audit', types: ['audit'], color: 'default' },
];

const CREDITS_MESSAGE = 'Google AI prepaid credits are depleted — a platform administrator needs to top up billing before the assistant can answer.';

const truncate = (s, n = 42) => {
  const str = String(s || '');
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
};

const dayKey = (d) => new Date(d).toDateString();
const dayLabel = (d) => new Date(d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const emptyActivityState = { events: [], systems: [], detections: 0, generated: null };
const emptyIncidentState = { events: [], hosts: [], users: [], assets: [], span: null, detections: 0, generated: null };

const groupKeyForType = (systemType) => (FILTER_GROUPS.find((g) => g.types.includes(systemType)) || {}).key;

// ATT&CK kill-chain stages, in order.
const KILL_CHAIN_STAGES = [
  'Initial Access',
  'Execution',
  'Persistence',
  'Privilege Escalation',
  'Defense Evasion',
  'Credential Access',
  'Discovery',
  'Lateral Movement',
  'Collection',
  'Command and Control',
  'Exfiltration',
  'Impact',
];

const WARNING_TACTICS = new Set(['Initial Access', 'Execution']);
const ERROR_TACTICS = new Set(['Credential Access', 'Lateral Movement', 'Privilege Escalation', 'Command and Control', 'Exfiltration', 'Impact']);
const tacticColor = (tactic) => {
  if (!tactic) return 'default';
  if (WARNING_TACTICS.has(tactic)) return 'warning';
  if (ERROR_TACTICS.has(tactic)) return 'error';
  return 'default';
};
const tacticDotColor = (tactic) => {
  const c = tacticColor(tactic);
  return c === 'default' ? 'grey.400' : `${c}.main`;
};

// Structured attack-event details: [key, label] in display order.
const DETAIL_FIELDS = [
  ['process', 'Process'],
  ['processPath', 'Path'],
  ['commandLine', 'Command line'],
  ['parentProcess', 'Parent'],
  ['parentCommandLine', 'Parent command line'],
  ['file', 'File'],
  ['sha256', 'SHA-256'],
  ['md5', 'MD5'],
  ['network', 'Network'],
  ['protocol', 'Protocol'],
  ['user', 'User'],
  ['hostIp', 'Host IP'],
  ['registryOrTask', 'Registry/Task'],
  ['outcome', 'Outcome'],
  ['vendorAlert', 'Vendor alert'],
  ['source', 'Source'],
  ['intel', 'Threat intel'],
  ['other', 'Other'],
];
const OUTCOME_SUCCESS = new Set(['blocked', 'quarantined', 'killed']);
const OUTCOME_WARNING = new Set(['allowed', 'detected-only']);
const outcomeColor = (v) => {
  const s = String(v || '').toLowerCase();
  if (OUTCOME_SUCCESS.has(s)) return 'success';
  if (OUTCOME_WARNING.has(s)) return 'warning';
  return 'default';
};
const hasValue = (v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);

const VERDICT_SEVERITY = {
  confirmed_intrusion: 'error',
  likely_malicious: 'error',
  suspicious: 'warning',
  benign: 'success',
  false_positive: 'success',
};
const VERDICT_LABEL = {
  confirmed_intrusion: 'Confirmed intrusion',
  likely_malicious: 'Likely malicious',
  suspicious: 'Suspicious',
  benign: 'Benign',
  false_positive: 'False positive',
};

const byTimeAsc = (a, b) => new Date(a.at) - new Date(b.at);

/**
 * Incident timeline for a case, in two views:
 *  - Attack timeline (default): what the malicious activity was, in order — host, user,
 *    process/command, destination and ATT&CK technique — reconstructed by AI into an
 *    assessment, attack timeline, kill chain, affected assets and attacker infrastructure.
 *    Case-management activity (statuses, assignments, comments, tickets) never appears here.
 *  - Activity log: the existing merged cross-system operational log (Excubia, vendors,
 *    AI/intel, automation, Jira, audit), with its own AI narration and filters.
 * Props: caseId, caseNumber, canUpdate, aiAvailable, aiError, aiCode, notice (setter fn), showError (fn).
 */
export default function InvestigationTimeline({
  caseId,
  caseNumber,
  canUpdate,
  aiAvailable,
  aiError,
  aiCode,
  notice,
  showError,
}) {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);

  // Attack timeline state
  const [incident, setIncident] = useState(emptyIncidentState);
  const [incidentLoading, setIncidentLoading] = useState(true);
  const [incidentRefreshing, setIncidentRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [incidentError, setIncidentError] = useState(null);
  const [incidentNotice, setIncidentNotice] = useState(null);

  // Activity log state
  const [data, setData] = useState(emptyActivityState);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [narrating, setNarrating] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localNotice, setLocalNotice] = useState(null);
  const [enabledGroups, setEnabledGroups] = useState(null);
  const [keyOnly, setKeyOnly] = useState(false);
  const [highlightId, setHighlightId] = useState(null);

  const formatCaseDateTime = useCallback((d) => formatDateTime(d, user?.preferences), [user]);

  const reportError = useCallback((err, fallback, setter) => {
    if (typeof showError === 'function') { showError(err, fallback); return; }
    const message = err?.response?.data?.message || fallback;
    (setter || setLocalError)(message);
  }, [showError]);

  const reportSuccess = useCallback((message, setter) => {
    if (typeof notice === 'function') { notice(message); return; }
    (setter || setLocalNotice)(message);
  }, [notice]);

  const loadIncident = useCallback(async ({ silent } = {}) => {
    if (!caseId) return;
    if (silent) setIncidentRefreshing(true); else setIncidentLoading(true);
    setIncidentError(null);
    try {
      const res = await apiService.cases.getIncidentTimeline(caseId);
      setIncident({
        events: res.data?.events || [],
        hosts: res.data?.hosts || [],
        users: res.data?.users || [],
        assets: res.data?.assets || [],
        span: res.data?.span || null,
        detections: res.data?.detections || 0,
        generated: res.data?.generated || null,
      });
    } catch (err) {
      reportError(err, 'Failed to load the attack timeline', setIncidentError);
    } finally {
      if (silent) setIncidentRefreshing(false); else setIncidentLoading(false);
    }
  }, [caseId, reportError]);

  const loadActivity = useCallback(async ({ silent } = {}) => {
    if (!caseId) return;
    if (silent) setRefreshing(true); else setLoading(true);
    setLocalError(null);
    try {
      const res = await apiService.cases.getTimeline(caseId);
      setData({
        events: res.data?.events || [],
        systems: res.data?.systems || [],
        detections: res.data?.detections || 0,
        generated: res.data?.generated || null,
      });
    } catch (err) {
      reportError(err, 'Failed to load investigation timeline');
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [caseId, reportError]);

  useEffect(() => { loadIncident(); loadActivity(); }, [loadIncident, loadActivity]);

  const handleRefreshAll = () => {
    loadIncident({ silent: true });
    loadActivity({ silent: true });
  };

  const handleGenerateIncident = async () => {
    if (!caseId) return;
    setGenerating(true);
    setIncidentError(null);
    try {
      const res = await apiService.cases.generateIncidentTimeline(caseId);
      setIncident({
        events: res.data?.events || [],
        hosts: res.data?.hosts || [],
        users: res.data?.users || [],
        assets: res.data?.assets || [],
        span: res.data?.span || null,
        detections: res.data?.detections || 0,
        generated: res.data?.generated || null,
      });
      reportSuccess('Attack timeline reconstructed', setIncidentNotice);
    } catch (err) {
      reportError(err, 'Failed to reconstruct the attack timeline', setIncidentError);
    } finally {
      setGenerating(false);
    }
  };

  const handleNarrate = async () => {
    if (!caseId) return;
    setNarrating(true);
    setLocalError(null);
    try {
      const res = await apiService.cases.generateTimeline(caseId);
      setData({
        events: res.data?.events || [],
        systems: res.data?.systems || [],
        detections: res.data?.detections || 0,
        generated: res.data?.generated || null,
      });
      reportSuccess('Investigation timeline narrated');
    } catch (err) {
      reportError(err, 'Failed to generate AI timeline narration');
    } finally {
      setNarrating(false);
    }
  };

  const presentGroupKeys = useMemo(() => {
    const types = new Set(data.events.map((e) => e.systemType));
    return FILTER_GROUPS.filter((g) => g.types.some((t) => types.has(t))).map((g) => g.key);
  }, [data.events]);

  useEffect(() => {
    if (enabledGroups === null && presentGroupKeys.length) {
      setEnabledGroups(new Set(presentGroupKeys));
    }
  }, [presentGroupKeys, enabledGroups]);

  const toggleGroup = (key) => {
    setEnabledGroups((prev) => {
      const base = new Set(prev === null ? presentGroupKeys : prev);
      if (base.has(key)) base.delete(key); else base.add(key);
      return base;
    });
  };

  const significanceById = useMemo(() => {
    const map = new Map();
    (data.generated?.events || []).forEach((e) => map.set(e.id, e));
    return map;
  }, [data.generated]);

  const visibleEvents = useMemo(() => data.events.filter((e) => {
    const groupKey = groupKeyForType(e.systemType);
    const groupOn = !groupKey || enabledGroups === null || enabledGroups.has(groupKey);
    if (!groupOn) return false;
    if (data.generated) {
      const sig = significanceById.get(e.id)?.significance;
      if (sig === 'noise') return false;
      if (keyOnly && sig !== 'key') return false;
    }
    return true;
  }), [data.events, data.generated, enabledGroups, significanceById, keyOnly]);

  const rows = useMemo(() => {
    const out = [];
    let last = null;
    visibleEvents.forEach((e) => {
      const k = dayKey(e.at);
      if (k !== last) { out.push({ kind: 'day', key: `day-${k}`, label: dayLabel(e.at) }); last = k; }
      out.push({ kind: 'event', key: e.id, event: e });
    });
    return out;
  }, [visibleEvents]);

  const scrollToPhase = (phase) => {
    const firstId = phase.eventIds && phase.eventIds[0];
    if (!firstId) return;
    const el = document.getElementById(`tl-event-${firstId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(firstId);
    setTimeout(() => setHighlightId((cur) => (cur === firstId ? null : cur)), 2000);
  };

  const disabledReason = aiCode === 'AI_CREDITS' ? CREDITS_MESSAGE : (aiError || 'AI is not configured');
  const generated = data.generated;

  const renderEventDetails = (details) => {
    if (!details) return null;
    if (typeof details === 'string') {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}
        >
          {details}
        </Typography>
      );
    }
    const fields = DETAIL_FIELDS.filter(([key]) => hasValue(details[key]));
    if (!fields.length) return null;
    return (
      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {fields.map(([key, label]) => {
          const value = details[key];
          if (key === 'outcome') {
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>{label}</Typography>
                <Chip size="small" label={value} color={outcomeColor(value)} />
              </Box>
            );
          }
          const text = Array.isArray(value) ? value.join('; ') : String(value);
          return (
            <Box key={key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0, pt: '1px' }}>{label}</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1, minWidth: 0 }}
              >
                {text}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderAttackEvent = (event, idx) => (
    <Box
      key={`${event.at || 'na'}-${idx}`}
      sx={{ display: 'flex', gap: 1.5, py: 1, px: 1 }}
    >
      <Box sx={{ pt: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: tacticDotColor(event.tactic) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Tooltip title={event.at ? formatRelativeTime(event.at) : ''}>
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {event.at ? formatCaseDateTime(event.at) : 'Unknown time'}
            </Typography>
          </Tooltip>
          {event.host && <Chip size="small" label={event.host} variant="outlined" />}
          {event.user && <Chip size="small" label={event.user} variant="outlined" />}
          {event.tactic && <Chip size="small" label={event.tactic} color={tacticColor(event.tactic)} />}
          {event.confidence === 'inferred' && <Chip size="small" label="inferred" variant="outlined" />}
        </Box>
        {event.technique && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', mt: 0.25 }}>
            {event.technique}
          </Typography>
        )}
        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>{event.action}</Typography>
        {renderEventDetails(event.details)}
        {(event.evidence || []).length > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
            {event.evidence.map((detId, i) => (
              <Link key={detId} component={RouterLink} to={`/dashboard/detections/${detId}`} variant="caption">
                {`evidence ${i + 1}`}
              </Link>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  const assessment = incident.generated?.assessment;
  const killChainByStage = useMemo(() => {
    const map = new Map();
    (incident.generated?.killChain || []).forEach((k) => map.set(k.stage, k));
    return map;
  }, [incident.generated]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={1}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <TimelineIcon color="primary" />
            Incident timeline
            {caseNumber ? (
              <Typography component="span" variant="body2" color="text.secondary">({caseNumber})</Typography>
            ) : null}
          </Typography>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={handleRefreshAll}
                disabled={incidentLoading || loading || incidentRefreshing || refreshing}
              >
                {(incidentRefreshing || refreshing) ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Attack timeline" />
          <Tab label="Activity log" />
        </Tabs>

        {tab === 0 && (
          <Box>
            {incidentError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setIncidentError(null)}>{incidentError}</Alert>
            )}
            {incidentNotice && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setIncidentNotice(null)}>{incidentNotice}</Alert>
            )}

            {incidentLoading ? (
              <Box display="flex" alignItems="center" gap={1} py={2}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading attack timeline...</Typography>
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                  <Typography variant="caption" color="text.secondary">
                    {incident.detections} detection{incident.detections === 1 ? '' : 's'}
                    {' · '}{incident.hosts.length} host{incident.hosts.length === 1 ? '' : 's'}
                    {' · '}{incident.users.length} user{incident.users.length === 1 ? '' : 's'}
                    {incident.span ? ` · ${formatCaseDateTime(incident.span.start)} → ${formatCaseDateTime(incident.span.end)}` : ''}
                  </Typography>
                  {canUpdate && (
                    <Tooltip title={aiAvailable ? '' : disabledReason}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={generating ? <CircularProgress size={14} /> : <AiIcon />}
                          onClick={handleGenerateIncident}
                          disabled={generating || !aiAvailable || incident.events.length === 0}
                        >
                          {generating ? 'Reconstructing...' : incident.generated ? 'Re-run' : 'Reconstruct with AI'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>

                {incident.events.length === 0 ? (
                  <Alert severity="info">No detections linked to this case yet.</Alert>
                ) : incident.generated ? (
                  <>
                    <Alert severity={VERDICT_SEVERITY[assessment?.verdict] || 'info'} sx={{ mb: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                        <Chip
                          size="small"
                          label={VERDICT_LABEL[assessment?.verdict] || assessment?.verdict || 'Unknown'}
                          color={VERDICT_SEVERITY[assessment?.verdict] || 'default'}
                        />
                        {assessment?.confidence !== undefined && assessment?.confidence !== null && (
                          <Typography variant="caption" color="text.secondary">confidence {assessment.confidence}%</Typography>
                        )}
                      </Box>
                      <Typography variant="body2">{assessment?.summary}</Typography>
                    </Alert>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Attack timeline</Typography>
                    <Box sx={{ mb: 2 }}>
                      {(incident.generated.attackTimeline || []).slice().sort(byTimeAsc).map((event, idx) => renderAttackEvent(event, idx))}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Kill chain</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                      {KILL_CHAIN_STAGES.map((stage) => {
                        const entry = killChainByStage.get(stage);
                        const status = entry?.status || 'not_observed';
                        return (
                          <Tooltip key={stage} title={entry?.note || stage}>
                            <Chip
                              size="small"
                              label={stage}
                              color={status === 'observed' ? 'error' : status === 'inferred' ? 'warning' : 'default'}
                              variant={status === 'observed' ? 'filled' : 'outlined'}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Affected assets</Typography>
                        {(incident.generated.affectedAssets || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">None identified.</Typography>
                        ) : (
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Host</TableCell>
                                  <TableCell>User</TableCell>
                                  <TableCell>Role</TableCell>
                                  <TableCell>First seen</TableCell>
                                  <TableCell>Last seen</TableCell>
                                  <TableCell>Impact</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {incident.generated.affectedAssets.map((a, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{a.host || '-'}</TableCell>
                                    <TableCell>{a.user || '-'}</TableCell>
                                    <TableCell>{a.role || '-'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{a.firstSeen ? formatCaseDateTime(a.firstSeen) : '-'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{a.lastSeen ? formatCaseDateTime(a.lastSeen) : '-'}</TableCell>
                                    <TableCell>{a.impact || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Attacker infrastructure</Typography>
                        {(incident.generated.attackerInfrastructure || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">None identified.</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {incident.generated.attackerInfrastructure.map((infra, i) => (
                              <Paper key={i} variant="outlined" sx={{ p: 1 }}>
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                  <Chip size="small" label={infra.type || 'unknown'} />
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>{infra.value}</Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {infra.role || '-'}{infra.intel ? ` · ${infra.intel}` : ''}
                                </Typography>
                              </Paper>
                            ))}
                          </Box>
                        )}
                      </Grid>
                    </Grid>

                    {(incident.generated.recommendedActions || []).length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle2">Recommended actions</Typography>
                        <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                          {incident.generated.recommendedActions.map((a, i) => (
                            <li key={i}><Typography variant="body2" color="text.secondary">{a}</Typography></li>
                          ))}
                        </ul>
                      </Box>
                    )}

                    {(incident.generated.gaps || []).length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle2">Gaps</Typography>
                        <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                          {incident.generated.gaps.map((g, i) => (
                            <li key={i}><Typography variant="body2" color="text.secondary">{g}</Typography></li>
                          ))}
                        </ul>
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      Reconstructed {incident.generated.generatedAt ? formatRelativeTime(incident.generated.generatedAt) : 'recently'}
                      {incident.generated.model ? ` · ${incident.generated.model}` : ''}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Reconstruct with AI to get the attack narrative, kill chain and affected assets.
                    </Alert>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Evidence timeline (from alerts)</Typography>
                    <Box>
                      {incident.events.slice().sort(byTimeAsc).map((event, idx) => renderAttackEvent(event, idx))}
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {data.events.length} event{data.events.length === 1 ? '' : 's'} across {data.systems.length} system{data.systems.length === 1 ? '' : 's'} from {data.detections} detection{data.detections === 1 ? '' : 's'}
            </Typography>

            <Box display="flex" justifyContent="flex-end" mb={1}>
              {canUpdate && (
                <Tooltip title={aiAvailable ? '' : disabledReason}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={narrating ? <CircularProgress size={14} /> : <AiIcon />}
                      onClick={handleNarrate}
                      disabled={narrating || !aiAvailable || loading || data.events.length === 0}
                    >
                      {narrating ? 'Narrating...' : generated ? 'Re-narrate with AI' : 'Narrate with AI'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>

            {localError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>{localError}</Alert>
            )}
            {localNotice && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLocalNotice(null)}>{localNotice}</Alert>
            )}

            {loading ? (
              <Box display="flex" alignItems="center" gap={1} py={2}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading timeline...</Typography>
              </Box>
            ) : data.events.length === 0 ? (
              <Alert severity="info">No investigation events yet.</Alert>
            ) : (
              <>
                {presentGroupKeys.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mb={2}>
                    {FILTER_GROUPS.filter((g) => presentGroupKeys.includes(g.key)).map((g) => {
                      const on = enabledGroups === null || enabledGroups.has(g.key);
                      return (
                        <Chip
                          key={g.key}
                          size="small"
                          clickable
                          label={g.label}
                          color={g.color}
                          variant={on ? 'filled' : 'outlined'}
                          onClick={() => toggleGroup(g.key)}
                          sx={!on ? { opacity: 0.6 } : undefined}
                        />
                      );
                    })}
                    <FormControlLabel
                      sx={{ ml: 1 }}
                      control={(
                        <Switch
                          size="small"
                          checked={keyOnly}
                          disabled={!generated}
                          onChange={(e) => setKeyOnly(e.target.checked)}
                        />
                      )}
                      label="Key events only"
                    />
                  </Box>
                )}

                {generated && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ mb: 1 }}>{renderMarkdown(generated.narrative)}</Box>

                    {(generated.phases || []).length > 0 && (
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Phase</TableCell>
                              <TableCell>Time range</TableCell>
                              <TableCell>Summary</TableCell>
                              <TableCell>MITRE</TableCell>
                              <TableCell align="right">Events</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {generated.phases.map((p, i) => (
                              <TableRow
                                key={i}
                                hover={!!(p.eventIds && p.eventIds.length)}
                                onClick={() => scrollToPhase(p)}
                                sx={{ cursor: p.eventIds && p.eventIds.length ? 'pointer' : 'default' }}
                              >
                                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{p.name}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {p.start ? formatCaseDateTime(p.start) : '-'} &rarr; {p.end ? formatCaseDateTime(p.end) : '-'}
                                </TableCell>
                                <TableCell>{p.summary}</TableCell>
                                <TableCell>
                                  <Box display="flex" gap={0.5} flexWrap="wrap">
                                    {(p.mitreTactics || []).map((t, ti) => (
                                      <Chip key={ti} size="small" label={t} variant="outlined" />
                                    ))}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">{(p.eventIds || []).length}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    {(generated.gaps || []).length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle2">Gaps to investigate</Typography>
                        <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                          {generated.gaps.map((g, i) => (
                            <li key={i}><Typography variant="body2" color="text.secondary">{g}</Typography></li>
                          ))}
                        </ul>
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      Narrated {generated.generatedAt ? formatRelativeTime(generated.generatedAt) : 'recently'}
                      {generated.model ? ` · ${generated.model}` : ''}
                      {generated.confidence !== undefined && generated.confidence !== null ? ` · confidence ${generated.confidence}%` : ''}
                    </Typography>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                )}

                {visibleEvents.length === 0 ? (
                  <Alert severity="info">No events match the current filters.</Alert>
                ) : (
                  <Box>
                    {rows.map((row) => {
                      if (row.kind === 'day') {
                        return (
                          <Typography key={row.key} variant="overline" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 0.5, letterSpacing: 1 }}>
                            {row.label}
                          </Typography>
                        );
                      }
                      const event = row.event;
                      const sig = significanceById.get(event.id);
                      const meta = TYPE_META[event.systemType] || { label: event.systemType, color: 'default' };
                      const isHighlighted = highlightId === event.id;
                      return (
                        <Box
                          id={`tl-event-${event.id}`}
                          key={row.key}
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            py: 1,
                            px: 1,
                            borderRadius: 1,
                            transition: 'background-color 0.6s',
                            bgcolor: isHighlighted ? 'action.selected' : 'transparent',
                          }}
                        >
                          <Box sx={{ pt: 0.75 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: event.systemType === 'audit' ? 'grey.600' : event.systemType === 'jira' ? 'grey.400' : `${meta.color}.main`,
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Tooltip title={formatRelativeTime(event.at)}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {formatCaseDateTime(event.at)}
                                </Typography>
                              </Tooltip>
                              <Chip
                                size="small"
                                label={meta.label}
                                color={meta.color}
                                variant={event.systemType === 'audit' ? 'filled' : 'outlined'}
                                sx={event.systemType === 'audit' ? { bgcolor: 'grey.600', color: 'common.white' } : undefined}
                              />
                              {event.severity && (
                                <Chip size="small" label={event.severity} variant="outlined" />
                              )}
                              {sig?.significance === 'key' && (
                                <Chip size="small" label="KEY" color="success" />
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>{event.action}</Typography>
                            {event.description && (
                              <Typography variant="body2" color="text.secondary">{event.description}</Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                              by {event.actor?.name || 'Unknown'} on {event.system}
                              {event.target?.type === 'detection' && (
                                <>
                                  {' · '}
                                  <Link component={RouterLink} to={event.target.link || `/dashboard/detections/${event.target.id}`}>
                                    {truncate(event.target.label)}
                                  </Link>
                                </>
                              )}
                            </Typography>
                            {sig?.summary && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                {sig.summary}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
